import { Dimensions, Platform } from 'react-native';
import type { ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { androidScaleFont, androidScaleSize } from '../../shared/theme/androidUiParity';
import { resolveWebLayoutWidth } from '../../shared/platform/webLayout';
import {
  JOURNEY_AGE_MAX,
  JOURNEY_AGE_MIN,
  JOURNEY_HEIGHT_MAX,
  JOURNEY_HEIGHT_MIN,
  JOURNEY_WEIGHT_MAX,
  JOURNEY_WEIGHT_MIN,
} from '../../data/domains/journey';

type WheelAccent = 'primary' | 'secondary' | 'tertiary';

/** Smooth, critically damped snap — settles cleanly after a fling. */
export const JOURNEY_WHEEL_SNAP_SPRING = {
  mass: 0.24,
  damping: 22,
  stiffness: 195,
  overshootClamping: true,
};

/** Ghost ticks past min/max so the strip still reads continuous at the ends. */
export const JOURNEY_WHEEL_OVERSCROLL_TICKS = 14;

const IS_ANDROID = Platform.OS === 'android';
const REFERENCE_WIDTH = 390;

const WHEEL_SCROLL_H = IS_ANDROID ? 56 : 48;
const WHEEL_READOUT_GUTTER = androidScaleSize(4);
const WHEEL_READOUT_GUTTER_BOTTOM = IS_ANDROID ? androidScaleSize(2) : androidScaleSize(2);
const WHEEL_LINE_MAX = androidScaleSize(22);
const WHEEL_LINE_MIN = androidScaleSize(8);
const WHEEL_LINE_CANVAS_PAD = IS_ANDROID ? androidScaleSize(2) : androidScaleSize(3);
const WHEEL_LABEL_NUDGE = 0;

function journey_stop_count(min: number, max: number) {
  return Math.max(1, max - min + 1);
}

/** ~14 ticks visible; keep spacing wide enough for precise finger control. */
export function journeyRulerSpacePerLine(stop_count: number) {
  const screen_w = resolveWebLayoutWidth(Dimensions.get('window').width);
  const track_w = Math.max(280, screen_w - androidScaleSize(56));
  const by_viewport = track_w / 14;
  const by_span = stop_count > 1 ? 2800 / (stop_count - 1) : by_viewport;
  const raw = Math.min(by_viewport, by_span);
  return androidScaleSize(Math.round(Math.min(16, Math.max(IS_ANDROID ? 11 : 10, raw))));
}

export function journeyDigitTypography(base_font_size: number) {
  const screen_w = resolveWebLayoutWidth(Dimensions.get('window').width);
  const width_factor = Math.min(1.1, Math.max(0.88, screen_w / REFERENCE_WIDTH));
  const count_font = androidScaleFont(Math.round(base_font_size * width_factor));
  const digit_width = androidScaleSize(Math.max(26, Math.ceil(count_font * 0.62)));
  const digit_height = androidScaleSize(Math.max(38, Math.ceil(count_font * 1.12)));
  return {
    countFontSize: count_font,
    textDigitWidth: digit_width,
    textDigitHeight: digit_height,
  };
}

function journey_ruler_layout(min: number, max: number, big_line_every: number) {
  return {
    fixedSpacePerLine: journeyRulerSpacePerLine(journey_stop_count(min, max)),
    bigLineEvery: big_line_every,
  } as const;
}

export function createJourneyWheelTheme(
  colors: ThemeColors,
  accent: WheelAccent,
  appearance: 'light' | 'dark' = 'light',
) {
  const accent_color = colors[accent];
  const accent_fade =
    accent === 'primary' ? colors.primaryFade : accent === 'secondary' ? colors.secondaryFade : colors.tertiaryFade;
  const digits = journeyDigitTypography(IS_ANDROID ? 36 : 40);
  const is_dark = appearance === 'dark';
  const tick_idle =
    (hexToRgba(colors.text, is_dark ? 0.22 : 0.11) as string) ||
    (hexToRgba(colors.borderLight, is_dark ? 0.9 : 1) as string) ||
    colors.border;
  const tick_major = (hexToRgba(accent_color, is_dark ? 0.95 : 0.88) as string) || accent_color;
  const marker = (hexToRgba(accent_color, is_dark ? 1 : 0.98) as string) || accent_color;

  return {
    fillContainer: false,
    showExpandButton: false,
    readoutLabelNudgeY: WHEEL_LABEL_NUDGE,
    readoutGutterV: WHEEL_READOUT_GUTTER,
    readoutGutterBottom: WHEEL_READOUT_GUTTER_BOTTOM,
    lineCanvasPaddingTop: WHEEL_LINE_CANVAS_PAD,
    maxLineHeight: WHEEL_LINE_MAX,
    minLineHeight: WHEEL_LINE_MIN,
    lineWidth: 2,
    scrollableAreaHeight: WHEEL_SCROLL_H,
    idleSliderArrowsAfterMs: 5000,
    snapSpring: JOURNEY_WHEEL_SNAP_SPRING,
    overscrollTicks: JOURNEY_WHEEL_OVERSCROLL_TICKS,
    lineColor: tick_idle,
    countColor: colors.text,
    backgroundColor: 'transparent',
    bigLineColor: tick_major,
    idleSliderArrowsColor: (hexToRgba(accent_color, is_dark ? 0.62 : 0.5) as string) || accent_color,
    scaleMarkerColor: marker,
    gradientAccentColor: accent === 'primary' ? 'transparent' : accent_fade,
    ...digits,
  } as const;
}

export function createJourneyAgeWheelTheme(
  colors: ThemeColors,
  appearance: 'light' | 'dark' = 'light',
) {
  const digits = journeyDigitTypography(IS_ANDROID ? 32 : 36);
  return {
    ...createJourneyWheelTheme(colors, 'primary', appearance),
    ...digits,
    ...journey_ruler_layout(JOURNEY_AGE_MIN, JOURNEY_AGE_MAX, 10),
    readoutAlign: 'center' as const,
    maxDigits: 3,
    simpleReadout: true,
    gradientAccentColor: 'transparent',
    idleSliderArrowsAfterMs: 0,
    lineWidth: 2,
    maxLineHeight: androidScaleSize(24),
    minLineHeight: androidScaleSize(10),
    scrollableAreaHeight: IS_ANDROID ? 60 : 52,
  };
}

export function createJourneyHeightWheelTheme(
  colors: ThemeColors,
  appearance: 'light' | 'dark' = 'light',
) {
  const digits = journeyDigitTypography(IS_ANDROID ? 26 : 28);
  return {
    ...createJourneyWheelTheme(colors, 'tertiary', appearance),
    ...digits,
    ...journey_ruler_layout(JOURNEY_HEIGHT_MIN, JOURNEY_HEIGHT_MAX, 10),
    readoutAlign: 'center' as const,
    maxDigits: 3,
    simpleReadout: false,
    readoutSplit: 'cm_slash_imperial' as const,
    gradientAccentColor: 'transparent',
    idleSliderArrowsAfterMs: 0,
    lineWidth: 2,
    maxLineHeight: androidScaleSize(24),
    minLineHeight: androidScaleSize(10),
    scrollableAreaHeight: IS_ANDROID ? 60 : 52,
  };
}

export function createJourneyWeightWheelTheme(
  colors: ThemeColors,
  appearance: 'light' | 'dark' = 'light',
) {
  const digits = journeyDigitTypography(IS_ANDROID ? 30 : 34);
  return {
    ...createJourneyWheelTheme(colors, 'secondary', appearance),
    ...digits,
    ...journey_ruler_layout(JOURNEY_WEIGHT_MIN, JOURNEY_WEIGHT_MAX, 10),
    readoutAlign: 'center' as const,
    maxDigits: 3,
    simpleReadout: true,
    gradientAccentColor: 'transparent',
    idleSliderArrowsAfterMs: 0,
    lineWidth: 2,
    maxLineHeight: androidScaleSize(24),
    minLineHeight: androidScaleSize(10),
    scrollableAreaHeight: IS_ANDROID ? 60 : 52,
  };
}
